<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="crtcreator-post-modal"
    :title="modalTitle"
    :ok-title="isFormLoading ? 'Saving...' : 'Save'"
    :ok-disabled="isFormLoading || isModalLoading"
    :cancel-disabled="isModalLoading"
    cancel-variant="outline-secondary"
    @ok="onSubmit($event)"
  >
    <div
      v-if="isModalLoading"
      class="text-center py-5"
    >
      <b-spinner
        small
        label="Loading..."
        class="mr-50"
      />
      <span class="text-muted">Loading...</span>
    </div>
    <b-form v-else>
      <b-form-group label-for="post-name">
        <template #label>
          <span class="d-inline-flex align-items-center">
            Name / Title
            <feather-icon
              icon="CopyIcon"
              size="14"
              class="text-secondary cursor-pointer ml-50"
              v-b-tooltip.hover
              title="Copy name"
              @click.stop="copyField(edited.name, 'Post name')"
            />
          </span>
        </template>
        <b-form-input id="post-name" v-model="edited.name" placeholder="Name" />
        <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
      </b-form-group>
      <b-form-group label-for="post-caption">
        <template #label>
          <span class="d-inline-flex align-items-center">
            Caption
            <feather-icon
              icon="CopyIcon"
              size="14"
              class="text-secondary cursor-pointer ml-50"
              v-b-tooltip.hover
              title="Copy caption"
              @click.stop="copyField(edited.caption, 'Caption')"
            />
          </span>
        </template>
        <b-form-textarea
          id="post-caption"
          v-model="edited.caption"
          rows="3"
          max-rows="8"
          placeholder="Optional caption"
        />
      </b-form-group>
      <b-form-group label-for="post-hashtags">
        <template #label>
          <span class="d-inline-flex align-items-center">
            Hashtags
            <feather-icon
              icon="CopyIcon"
              size="14"
              class="text-secondary cursor-pointer ml-50"
              v-b-tooltip.hover
              title="Copy hashtags"
              @click.stop="copyField(edited.hashtags, 'Hashtags')"
            />
          </span>
        </template>
        <b-form-textarea
          id="post-hashtags"
          v-model="edited.hashtags"
          rows="2"
          max-rows="6"
          placeholder="Optional hashtags"
        />
      </b-form-group>
      <b-form-group label="Resource" label-for="post-resource">
        <ImageTextSelect
          id="post-resource"
          v-model="edited.crtcreator_resource_id"
          :options="resourceSelectOptions"
          image-key="icon"
          placeholder="Select resource"
        />
        <small v-if="errors.crtcreator_resource_id" class="text-danger">{{ errors.crtcreator_resource_id }}</small>
      </b-form-group>
      <b-form-group v-if="!lockContentId" label="Content" label-for="post-content">
        <b-form-select
          id="post-content"
          v-model="edited.crtcreator_content_id"
          :options="contentOptions"
        />
      </b-form-group>
      <b-form-group label="Post type" label-for="post-type">
        <v-select
          id="post-type"
          v-model="edited.crtcreator_post_type_id"
          :options="postTypeSelectOptions"
          label="name"
          :reduce="(item) => item.id"
          :clearable="true"
          placeholder="Start typing post type"
        />
      </b-form-group>
      <b-form-group label="ID Key" label-for="post-id-key">
        <b-form-input id="post-id-key" v-model="edited.id_key" />
      </b-form-group>
      <b-form-group label="URL" label-for="post-url">
        <b-form-input id="post-url" v-model="edited.url" />
      </b-form-group>
      <b-form-group label="Schedule" label-for="post-schedule-date">
        <div class="d-flex align-items-center flex-nowrap post-schedule-row">
          <b-form-datepicker
            id="post-schedule-date"
            v-model="scheduleDate"
            class="post-schedule-date"
            placeholder="Date"
            reset-button
            @input="onScheduleDatePicked"
          />
          <b-form-input
            id="post-schedule-time"
            ref="scheduleTimeInput"
            v-model="scheduleTime"
            type="time"
            class="post-schedule-time ml-1"
          />
        </div>
        <small v-if="errors.schedule" class="text-danger">{{ errors.schedule }}</small>
      </b-form-group>
      <b-form-group
        v-show="false"
        label="Password"
        label-for="post-password"
      >
        <b-form-input id="post-password" v-model="edited.password" type="text" />
      </b-form-group>
      <b-form-group
        v-if="showRemovedSwitch"
        label="Removed"
        label-for="post-removed"
      >
        <b-form-checkbox
          id="post-removed"
          v-model="edited.removed"
          switch
          :value="1"
          :unchecked-value="0"
        >
          Mark as removed
        </b-form-checkbox>
      </b-form-group>
      <b-form-group label="File name" label-for="post-file-name">
        <b-form-input
          id="post-file-name"
          v-model="edited.file_name"
          placeholder="Optional file name"
        />
      </b-form-group>
      <b-form-group label="Assigned to" label-for="post-assigned">
        <ImageTextSelect
          id="post-assigned"
          v-model="edited.assigned"
          :options="staffSelectOptions"
          image-key="picture"
          :clearable="true"
          placeholder="Select staff member"
        />
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BForm,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { normalizeImageUrl } from '@/libs/utils'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'
import {
  companyTz,
  moment,
  parseApiInstant,
  toUtcIso,
} from '@/libs/companyTime'

const defaults = () => ({
  id: null,
  name: '',
  caption: '',
  hashtags: '',
  assigned: null,
  crtcreator_resource_id: null,
  crtcreator_content_id: null,
  crtcreator_post_type_id: null,
  id_key: '',
  url: '',
  password: '',
  removed: 0,
  previous_post_id: null,
  file_name: '',
})

function splitSchedule(value) {
  const m = parseApiInstant(value)
  if (!m || !m.isValid()) {
    return { date: null, time: '' }
  }
  return {
    date: m.format('YYYY-MM-DD'),
    time: m.format('HH:mm'),
  }
}

function scheduleDateToYmd(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/)
    return match ? match[1] : null
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).tz(companyTz()).format('YYYY-MM-DD')
  }
  return null
}

function nullableText(value) {
  const trimmed = String(value || '').trim()
  return trimmed || null
}

export default {
  components: {
    BForm,
    BFormCheckbox,
    BFormDatepicker,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BModal,
    BSpinner,
    ImageTextSelect,
    'v-select': vSelect,
  },
  props: {
    record: { type: Object, default: null },
    clickDate: { type: Date, default: null },
    lockContentId: { type: [Number, String], default: null },
  },
  emits: ['saved'],
  data() {
    return {
      showModal: false,
      isModalLoading: false,
      isFormLoading: false,
      edited: defaults(),
      scheduleDate: null,
      scheduleTime: '',
      errors: {},
      openSeq: 0,
    }
  },
  computed: {
    ...mapGetters({
      resources: 'crtcreator/resources/items',
      contentItems: 'crtcreator/content/items',
      postTypes: 'crtcreator/postTypes/items',
      staff: 'staff/staff',
    }),
    modalTitle() {
      if (this.isModalLoading) {
        return this.record?.id ? 'Edit post' : 'Add post'
      }
      return this.edited.id ? 'Edit post' : 'Add post'
    },
    staffSelectOptions() {
      return (this.staff || []).map((member) => ({
        id: member.id,
        name: member.name,
        picture: normalizeImageUrl(member.picture),
      }))
    },
    resourceSelectOptions() {
      return (this.resources || []).map((r) => ({
        id: r.id,
        name: r.name,
        icon: r.social_resource_type?.icon || null,
      }))
    },
    contentOptions() {
      return [
        { value: null, text: 'None' },
        ...(this.contentItems || []).map((c) => ({ value: c.id, text: c.name })),
      ]
    },
    postTypeSelectOptions() {
      return (this.postTypes || []).map((t) => ({
        id: t.id,
        name: t.name,
      }))
    },
    selectedResource() {
      const id = this.edited.crtcreator_resource_id
      if (!id) return null
      return (this.resources || []).find((r) => Number(r.id) === Number(id)) || null
    },
    resourceTypeName() {
      return String(this.selectedResource?.social_resource_type?.name || '')
    },
    isFacebookResource() {
      return this.resourceTypeName.toLowerCase().includes('facebook')
    },
    isTikTokResource() {
      return this.resourceTypeName.toLowerCase().includes('tiktok')
    },
    hasPostUrl() {
      return Boolean(String(this.edited.url || '').trim())
    },
    showRemovedSwitch() {
      // Soft-remove only when editing a post that already has a URL.
      // Empty-URL posts are hard-deleted from the list instead.
      return Boolean(this.edited.id) && this.hasPostUrl
    },
  },
  watch: {
    async clickDate() {
      const seq = ++this.openSeq
      this.errors = {}
      this.edited = defaults()
      this.scheduleDate = null
      this.scheduleTime = ''
      this.isModalLoading = true
      this.showModal = true

      await this.ensureStaffLoaded()
      if (seq !== this.openSeq) {
        return
      }

      if (this.record && this.record.id) {
        const schedule = splitSchedule(this.record.schedule)
        this.edited = {
          id: this.record.id,
          name: this.record.name || '',
          caption: this.record.caption || '',
          hashtags: this.record.hashtags || '',
          assigned: this.record.assigned || null,
          crtcreator_resource_id: this.record.crtcreator_resource_id || null,
          crtcreator_content_id: this.record.crtcreator_content_id || null,
          crtcreator_post_type_id: this.record.crtcreator_post_type_id || null,
          id_key: this.record.id_key || '',
          url: this.record.url || '',
          password: this.record.password || '',
          removed: Number(this.record.removed) || 0,
          previous_post_id: this.record.previous_post_id || null,
          file_name: this.record.file_name || '',
        }
        this.scheduleDate = schedule.date
        this.scheduleTime = schedule.time
      } else {
        const schedule = splitSchedule(this.record?.schedule)
        this.edited = {
          ...defaults(),
          name: this.record?.name || '',
          caption: this.record?.caption || '',
          hashtags: this.record?.hashtags || '',
          assigned: this.record?.assigned || null,
          crtcreator_resource_id: this.record?.crtcreator_resource_id || null,
          crtcreator_content_id: this.lockContentId
            || this.record?.crtcreator_content_id
            || null,
          crtcreator_post_type_id: this.record?.crtcreator_post_type_id || null,
          password: this.record?.password || '',
          previous_post_id: this.record?.previous_post_id || null,
          file_name: this.record?.file_name || '',
        }
        this.scheduleDate = schedule.date
        this.scheduleTime = schedule.time
      }
      this.isModalLoading = false
    },
    'edited.id_key'() {
      this.syncDerivedUrl()
    },
    'edited.crtcreator_resource_id'() {
      this.syncDerivedUrl()
    },
    'edited.url'() {
      if (!this.hasPostUrl && Number(this.edited.removed) === 1) {
        this.edited.removed = 0
      }
    },
  },
  methods: {
    ...mapActions({
      addItem: 'crtcreator/posts/addItem',
      updateItem: 'crtcreator/posts/updateItem',
      loadStaff: 'staff/loadStaff',
    }),
    async ensureStaffLoaded() {
      if ((this.staff || []).length) {
        return
      }
      try {
        await this.loadStaff()
      } catch (e) {
        // Staff list may be restricted; leave dropdown empty.
      }
    },
    async copyField(value, label) {
      const text = String(value || '')
      if (!text.trim()) {
        toastFailure(`Nothing to copy for ${label.toLowerCase()}`)
        return
      }
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const input = document.createElement('textarea')
          input.value = text
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        toastSuccess(`${label} copied`)
      } catch (e) {
        toastFailure('Failed to copy')
      }
    },
    syncDerivedUrl() {
      const idKey = String(this.edited.id_key || '').trim()
      if (!idKey) {
        return
      }
      if (this.isFacebookResource) {
        this.edited.url = `https://www.facebook.com/watch/?v=${idKey}`
        return
      }
      if (this.isTikTokResource) {
        const resourceUrl = String(this.selectedResource?.url || '').trim().replace(/\/+$/, '')
        if (resourceUrl) {
          this.edited.url = `${resourceUrl}/video/${idKey}`
        }
      }
    },
    onScheduleDatePicked() {
      // Wait for the datepicker to finish closing so it does not steal focus back.
      this.$nextTick(() => {
        setTimeout(() => {
          const ref = this.$refs.scheduleTimeInput
          const el = ref?.$el || ref
          if (el && typeof el.focus === 'function') {
            el.focus()
            if (typeof el.select === 'function') {
              el.select()
            }
          }
        }, 50)
      })
    },
    buildSchedulePayload() {
      const ymd = scheduleDateToYmd(this.scheduleDate)
      if (!ymd) {
        return null
      }
      const time = String(this.scheduleTime || '00:00').slice(0, 5)
      // Form values are company-local wall clock → store as UTC ISO.
      return toUtcIso(`${ymd} ${time}`)
    },
    async onSubmit(event) {
      event.preventDefault()
      if (this.isModalLoading) {
        return
      }
      this.errors = {}
      if (!String(this.edited.name || '').trim()) {
        this.errors.name = 'Name is required'
      }
      if (!this.edited.crtcreator_resource_id) {
        this.errors.crtcreator_resource_id = 'Resource is required'
      }
      if (!this.scheduleDate) {
        this.errors.schedule = 'Schedule is required'
      }
      if (Object.keys(this.errors).length) {
        return
      }
      this.syncDerivedUrl()
      this.isFormLoading = true
      const contentId = this.lockContentId || this.edited.crtcreator_content_id || null
      const payload = {
        name: this.edited.name,
        caption: nullableText(this.edited.caption),
        hashtags: nullableText(this.edited.hashtags),
        assigned: this.edited.assigned || null,
        crtcreator_resource_id: this.edited.crtcreator_resource_id || null,
        crtcreator_content_id: contentId,
        crtcreator_post_type_id: this.edited.crtcreator_post_type_id || null,
        id_key: this.edited.id_key || null,
        url: this.edited.url || null,
        schedule: this.buildSchedulePayload(),
        password: this.edited.password || null,
        removed: this.showRemovedSwitch ? (Number(this.edited.removed) || 0) : 0,
        previous_post_id: this.edited.previous_post_id || null,
        file_name: nullableText(this.edited.file_name),
      }
      try {
        if (this.edited.id) {
          await this.updateItem({ id: this.edited.id, payload })
          toastSuccess('Successfully updated')
        } else {
          await this.addItem(payload)
          toastSuccess('Successfully added')
        }
        this.showModal = false
        this.$emit('saved')
      } catch (e) {
        const payloadErrors = e?.response?.data
        if (payloadErrors && typeof payloadErrors === 'object') {
          const next = {}
          ;['name', 'crtcreator_resource_id', 'schedule'].forEach((field) => {
            if (payloadErrors[field]) {
              next[field] = Array.isArray(payloadErrors[field])
                ? payloadErrors[field][0]
                : payloadErrors[field]
            }
          })
          if (Object.keys(next).length) {
            this.errors = next
            return
          }
        }
        toastFailure('Failed to save')
      } finally {
        this.isFormLoading = false
      }
    },
  },
}
</script>

<style scoped>
.post-schedule-row {
  gap: 0.25rem;
}

.post-schedule-date {
  flex: 1 1 auto;
  min-width: 0;
}

.post-schedule-time {
  flex: 0 0 8.5rem;
  max-width: 8.5rem;
}
</style>
